"use client";

import clsx from "clsx";
import {
  useEffect,
  useRef,
  useState,
  type ComponentType,
  type FormEvent,
} from "react";
import FormSteps from "@/components/forms/FormSteps";
import CheckboxField from "@/components/forms/fields/CheckboxField";
import RadioGroupField from "@/components/forms/fields/RadioGroupField";
import SelectField from "@/components/forms/fields/SelectField";
import TextareaField from "@/components/forms/fields/TextareaField";
import TextField from "@/components/forms/fields/TextField";
import type { FieldProps } from "@/components/forms/fields/shared";
import type {
  FieldDefinition,
  FieldErrors,
  FieldType,
  FormMessage,
  FormValues,
} from "@/components/forms/types";
import {
  lowestStep,
  requiredIfMet,
  validateFields,
} from "@/components/forms/validate";
import { submitForm } from "@/lib/forms";
import {
  AUTOCOMPLETE_TOKENS,
  GROUP_NOTES,
  HALF_WIDTH_FIELDS,
  REGISTRATION_FIELDS,
  STEP_INTROS,
  STEP_LABELS,
} from "./fields";
import LocationSelect from "./LocationSelect";
import RegistrationComplete from "./RegistrationComplete";

/**
 * Which partial renders which field type. The three single-line types share
 * one partial - same chrome, different input type - and anything not listed
 * here is skipped rather than guessed at.
 */
const PARTIALS: Record<FieldType, ComponentType<FieldProps>> = {
  text: TextField,
  email: TextField,
  tel: TextField,
  paragraph: TextareaField,
  select: SelectField,
  radio: RadioGroupField,
  checkbox: CheckboxField,
};

/** The form's buttons: Continue (or Register) and Back. */
const actionClass = clsx(
  "inline-flex cursor-pointer items-center gap-2 rounded-[100px] py-[13px] text-[14px] leading-[1.2] tracking-[-0.01em]",
  "transition-[background-color,border-color,translate] duration-[200ms,180ms,150ms] ease-[ease]",
);

/** The size and the slate the form's secondary copy is set in. */
const slateText = "text-[13.5px] leading-[1.65] text-[#5a6a7a]";

type PendingFocus =
  { kind: "step"; step: number } | { kind: "complete" } | null;

/**
 * The port of fragments/forms/das-register-form.php and the multi-step engine
 * in form.js that drove it: step 1 is the pair of approval gates, step 2 is the
 * applicant's details. Every field comes from REGISTRATION_FIELDS and is
 * rendered by the partial that matches its type, so the form is never
 * described twice.
 *
 * Both steps are rendered at once and the inactive one carries the hidden
 * attribute. Each step is checked against its fields' rules before every move,
 * and the last step's button sends the answers through submitForm().
 *
 * The nav and the body are siblings, never nested. On success the body is
 * replaced by the confirmation, and the nav has to outlive that in order to
 * move to its Complete item.
 *
 * The form's state is data-state on the form - loading while it sends, error
 * once a submission has failed, complete once one has been accepted - and
 * data-message while the message box has something to say. The message box
 * and the submit button style themselves from it.
 */
export default function DasRegisterForm() {
  const fields = REGISTRATION_FIELDS;

  const [values, setValues] = useState<FormValues>(() =>
    syncConditionals(initialValues(fields), fields),
  );
  const [errors, setErrors] = useState<FieldErrors>({});
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<FormMessage | null>(null);
  const [failed, setFailed] = useState(false);
  const [accepted, setAccepted] = useState<{
    firstName: string;
    organisationName: string;
  } | null>(null);

  const formRef = useRef<HTMLFormElement>(null);
  const messageRef = useRef<HTMLDivElement>(null);
  const honeypotRef = useRef<HTMLInputElement>(null);
  const pendingFocus = useRef<PendingFocus>(null);

  /*
   * Focusing a field clears the error on it, which is right when someone goes
   * back to a field to fix it and wrong when we are the ones putting them
   * there. This says which of the two is happening.
   */
  const focusingError = useRef(false);

  // However many steps the form is actually made of, rather than however many it claims.
  const steps = STEP_LABELS.map((_, index) => index + 1).filter((number) =>
    Object.values(fields).some((field) => field.step === number),
  );

  const totalSteps = steps.length;
  const complete = accepted !== null;

  /*
   * Focus moves once the step it is moving into has been revealed, so it waits
   * for the render that reveals it: focus cannot land on a field that is still
   * hidden.
   */
  useEffect(() => {
    const target = pendingFocus.current;
    const form = formRef.current;

    if (!target || !form) {
      return;
    }

    pendingFocus.current = null;

    if (target.kind === "complete") {
      // The confirmation is the answer to the button that was just pressed, so it is where focus belongs.
      form.querySelector<HTMLElement>('.js-form-body [tabindex="-1"]')?.focus();

      return;
    }

    const stepEl = form.querySelector<HTMLElement>(
      `.js-form-multistep-step[data-index="${target.step}"]`,
    );

    if (!stepEl) {
      return;
    }

    if (stepEl.querySelector('.js-field[data-error="true"]')) {
      focusFirstError(stepEl, focusingError);

      return;
    }

    firstVisible(stepEl.querySelectorAll<HTMLElement>(".js-field-el"))?.focus();
  });

  const handleChange = (name: string, value: string) => {
    const next = syncConditionals({ ...values, [name]: value }, fields);

    setValues(next);

    // A conditional field that has just gone is not being asked any more, so neither is its error.
    const gone = Object.keys(fields).filter(
      (key) =>
        fields[key].validation?.required_if && !isShown(fields[key], next),
    );

    if (gone.some((key) => errors[key])) {
      setErrors((current) => omit(current, gone));
    }
  };

  const handleFocus = (name: string) => {
    if (focusingError.current || !errors[name]) {
      return;
    }

    setErrors((current) => omit(current, [name]));
  };

  const goTo = (number: number) => {
    setStep(number);
    pendingFocus.current = { kind: "step", step: number };
  };

  const handleNext = () => {
    // A form that has been completed has no steps left to move between.
    if (complete || loading) {
      return;
    }

    setMessage(null);
    setFailed(false);

    /*
     * The step is checked against the same rules, in the same words, as the
     * server checks the whole submission, so being stopped here reads exactly
     * like being stopped there.
     */
    const stepErrors = validateFields(fields, values, step);
    const stepFieldNames = Object.keys(fields).filter(
      (key) => fields[key].step === step,
    );

    setErrors((current) => ({
      ...omit(current, stepFieldNames),
      ...stepErrors,
    }));

    if (Object.keys(stepErrors).length) {
      pendingFocus.current = { kind: "step", step };

      return;
    }

    if (step < totalSteps) {
      goTo(step + 1);

      return;
    }

    void send();
  };

  const handlePrev = () => {
    if (complete || loading) {
      return;
    }

    setMessage(null);
    setFailed(false);

    if (step > 1) {
      goTo(step - 1);
    }
  };

  const send = async () => {
    setLoading(true);
    setErrors({});

    /*
     * The honeypot travels with the answers. The theme's handler answered a
     * filled-in honeypot with a success and wrote nothing; that is the
     * backend's call to make, so it is passed on rather than judged here.
     */
    const data: Record<string, string> = {
      ...values,
      das_website_url: honeypotRef.current?.value ?? "",
    };

    let result;

    try {
      result = await submitForm("centre-registration", data);
    } catch {
      result = null;
    }

    setLoading(false);

    if (!result) {
      setFailed(true);
      setMessage({
        header: "Server error.",
        body: "Please try again later or contact the DAS team.",
      });
      scrollToMessage(messageRef);

      return;
    }

    if (result.ok) {
      setAccepted({
        firstName: values.first_name ?? "",
        organisationName: values.organisation_name ?? "",
      });
      pendingFocus.current = { kind: "complete" };

      return;
    }

    setFailed(true);
    setMessage({ body: result.message });

    const returned = result.errors ?? {};
    const names = Object.keys(returned);

    if (names.length) {
      setErrors(returned);
      goTo(lowestStep(fields, names));

      return;
    }

    scrollToMessage(messageRef);
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    handleNext();
  };

  const state = complete
    ? "complete"
    : loading
      ? "loading"
      : failed
        ? "error"
        : undefined;

  return (
    <form
      ref={formRef}
      action="?"
      className="group/form text-[15px] leading-[1.72] text-brand-blue js-form js-form-multistep-client-side-validation"
      data-current-step={step}
      data-state={state}
      data-message={message ? "" : undefined}
      onSubmit={handleSubmit}
      noValidate
    >
      <noscript>
        <p className="m-0 mb-8 rounded-[10px] border border-[#b91c1c]/20 border-l-3 border-l-[#b91c1c] bg-[#b91c1c]/4 px-[18px] py-3.5 text-[13.5px] leading-[1.65] text-brand-blue">
          This registration form needs JavaScript to send your answers. Please
          turn JavaScript on and reload the page, or contact the DAS team and we
          will take your registration another way.
        </p>
      </noscript>

      <FormSteps
        steps={STEP_LABELS}
        current={complete ? "complete" : step}
        label="Registration progress"
      />

      {/* Only failures have anything to report here; success is the form's own confirmation. */}
      <div
        ref={messageRef}
        className={clsx(
          "m-0 mb-7 hidden rounded-[10px] border-l-3 border-l-brand-blue/20 bg-brand-blue/3 px-5 py-4 group-data-message/form:block",
          "group-data-[state=error]/form:border-l-[#b91c1c] group-data-[state=error]/form:bg-[#b91c1c]/4 js-form-message",
        )}
        role="alert"
      >
        <h3 className="m-0 text-[15px] leading-[1.4] font-semibold text-brand-blue empty:hidden js-form-message-head">
          {message?.header || ""}
        </h3>

        <p
          className={`mx-0 mt-1.5 mb-0 ${slateText} empty:hidden js-form-message-body`}
        >
          {message?.body || ""}
        </p>
      </div>

      <div className="js-form-body">
        {complete ? (
          <RegistrationComplete
            firstName={accepted.firstName}
            organisationName={accepted.organisationName}
          />
        ) : (
          steps.map((number) => {
            const stepFields = Object.entries(fields).filter(
              ([, field]) => field.step === number,
            );
            const intro = STEP_INTROS[number];
            const isFirst = number === 1;
            const isLast = number === totalSteps;
            const label = STEP_LABELS[number - 1];

            return (
              <section
                key={number}
                className={clsx(
                  number !== step && "hidden",
                  "js-form-multistep-step",
                )}
                data-index={number}
                aria-label={`Step ${number} of ${totalSteps}: ${label}`}
                hidden={number !== step}
              >
                {/* No bottom margin: the theme's h3[class] reset outranked this heading's own margin, and staging shows none. */}
                {intro?.title && (
                  <h3 className="m-0 text-[length:clamp(22px,3vw,32px)] leading-[1.15] font-bold tracking-[-0.025em] text-brand-blue">
                    {intro.title}
                  </h3>
                )}

                {intro?.text &&
                  intro.text
                    .split(/[\r\n]+/)
                    .filter((line) => line.trim() !== "")
                    .map((line, index) => (
                      <p
                        key={index}
                        className="m-0 mb-10 max-w-[620px] text-[15px] leading-[1.8] text-[#5a6a7a]"
                      >
                        {line}
                      </p>
                    ))}

                {groupFields(stepFields).map(([group, groupFieldSet]) => (
                  <div
                    key={group || "_"}
                    className={clsx(
                      "m-0 border-0 p-0 not-first-of-type:border-t not-first-of-type:border-t-brand-blue/8 not-first-of-type:pt-9",
                      "mobile:not-first-of-type:pt-7 [&+&]:mt-9 mobile:[&+&]:mt-7",
                    )}
                  >
                    {group && (
                      <div className="mb-6">
                        <h3 className="m-0 flex items-center gap-2.5 p-0 text-[16px] leading-[1.3] font-bold tracking-[-0.01em] text-brand-blue before:h-[18px] before:w-[3px] before:shrink-0 before:rounded-[2px] before:bg-das-green before:content-['']">
                          {group}
                        </h3>
                      </div>
                    )}

                    <div>
                      {GROUP_NOTES[group] && (
                        <p
                          className={`m-0 mb-6 rounded-[10px] border border-brand-blue/6 border-l-3 border-l-brand-blue/19 bg-brand-blue/3 px-[18px] py-3.5 ${slateText}`}
                        >
                          {GROUP_NOTES[group]}
                        </p>
                      )}

                      <div className="flex flex-wrap gap-x-8 gap-y-7 mobile:gap-6">
                        {groupFieldSet.map(([name, field]) => {
                          const Partial =
                            name === "proposed_location"
                              ? LocationSelect
                              : PARTIALS[field.type];

                          if (!Partial) {
                            return null;
                          }

                          return (
                            <Partial
                              key={name}
                              id={`das-register-${name.replace(/_/g, "-")}`}
                              name={name}
                              field={field}
                              width={
                                HALF_WIDTH_FIELDS.includes(name) ? "half" : ""
                              }
                              autocomplete={AUTOCOMPLETE_TOKENS[name] || ""}
                              value={values[name] ?? ""}
                              error={errors[name]}
                              shown={isShown(field, values)}
                              onChange={handleChange}
                              onFocus={handleFocus}
                            />
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ))}

                <div className="mt-9 flex flex-wrap items-center gap-3">
                  <button
                    className={clsx(
                      actionClass,
                      "border-0 bg-brand-blue px-7 font-semibold text-white hover:-translate-y-px",
                      "group-data-[state=loading]/form:cursor-default group-data-[state=loading]/form:bg-brand-blue/53",
                      "group-data-[state=loading]/form:translate-none group-data-[state=loading]/form:hover:translate-none",
                      "js-form-multistep-next",
                    )}
                    type="button"
                    onClick={handleNext}
                  >
                    <span className="group-data-[state=loading]/form:hidden">
                      {isLast ? "Register your interest" : "Continue"}
                    </span>

                    <svg
                      className="shrink-0 group-data-[state=loading]/form:hidden"
                      width="13"
                      height="13"
                      viewBox="0 0 13 13"
                      fill="none"
                      aria-hidden="true"
                      focusable="false"
                    >
                      <path
                        d="M2 6.5h9M7 2l4.5 4.5L7 11"
                        stroke="currentColor"
                        strokeWidth="1.7"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>

                    {isLast && (
                      <span className="hidden items-center gap-2 group-data-[state=loading]/form:inline-flex">
                        <svg
                          className="shrink-0 animate-spin"
                          width="14"
                          height="14"
                          viewBox="0 0 14 14"
                          fill="none"
                          aria-hidden="true"
                          focusable="false"
                        >
                          <circle
                            cx="7"
                            cy="7"
                            r="5.5"
                            stroke="currentColor"
                            strokeOpacity="0.35"
                            strokeWidth="1.5"
                          />

                          <path
                            d="M7 1.5A5.5 5.5 0 0 1 12.5 7"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                          />
                        </svg>
                        Sending registration…
                      </span>
                    )}
                  </button>

                  {!isFirst && (
                    <button
                      className={clsx(
                        actionClass,
                        "border-[1.5px] border-brand-blue/13 bg-transparent px-5 font-medium text-brand-blue hover:border-brand-blue/27",
                        "js-form-multistep-prev",
                      )}
                      type="button"
                      onClick={handlePrev}
                    >
                      <svg
                        className="shrink-0"
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                        fill="none"
                        aria-hidden="true"
                        focusable="false"
                      >
                        <path
                          d="M8 2L3 6l5 4"
                          stroke="currentColor"
                          strokeWidth="1.6"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>

                      <span>Back</span>
                    </button>
                  )}
                </div>
              </section>
            );
          })
        )}
      </div>

      {/* The foot goes with the body on success: the honeypot has nothing left to guard. */}
      {!complete && (
        <div className="js-form-foot">
          {/*
           * The honeypot. It is taken out of the layout rather than out of the
           * document, because display:none is the one thing every bot checks
           * for. Nothing reaches it by keyboard or by screen reader, so anything
           * that arrives with it filled in was not a person.
           */}
          <div
            className="absolute! m-[-1px] h-px w-px overflow-hidden border-0 p-0 whitespace-nowrap [clip-path:inset(50%)] [clip:rect(0_0_0_0)]"
            aria-hidden="true"
          >
            <label htmlFor="das-register-website-url">
              Leave this field empty
            </label>

            <input
              ref={honeypotRef}
              type="text"
              id="das-register-website-url"
              name="das_website_url"
              defaultValue=""
              autoComplete="off"
              tabIndex={-1}
            />
          </div>
        </div>
      )}
    </form>
  );
}

function initialValues(fields: Record<string, FieldDefinition>): FormValues {
  const values: FormValues = {};

  for (const [name, field] of Object.entries(fields)) {
    if (field.type === "checkbox") {
      values[name] = field.checked || field.value ? field.value || "1" : "";
    } else {
      values[name] = field.value ?? "";
    }
  }

  return values;
}

/** A field with a "required_if" rule is only shown while the rule is met. */
function isShown(field: FieldDefinition, values: FormValues) {
  return (
    !field.validation?.required_if ||
    requiredIfMet(field.validation.required_if, values)
  );
}

/** An answer to a question that is no longer being asked is not an answer. */
function syncConditionals(
  values: FormValues,
  fields: Record<string, FieldDefinition>,
): FormValues {
  const next = { ...values };

  for (const [name, field] of Object.entries(fields)) {
    if (!isShown(field, next)) {
      next[name] = "";
    }
  }

  return next;
}

/** A step's fields under their group heading, in the order they are declared. */
function groupFields(stepFields: [string, FieldDefinition][]) {
  const groups = new Map<string, [string, FieldDefinition][]>();

  for (const entry of stepFields) {
    const group = entry[1].group || "";

    if (!groups.has(group)) {
      groups.set(group, []);
    }

    groups.get(group)!.push(entry);
  }

  return [...groups.entries()];
}

function omit(errors: FieldErrors, names: string[]): FieldErrors {
  const next = { ...errors };

  names.forEach((name) => delete next[name]);

  return next;
}

/** jQuery's :visible: laid out, so not inside anything hidden. */
function firstVisible(elements: NodeListOf<HTMLElement>) {
  return Array.from(elements).find(
    (element) => element.getClientRects().length > 0,
  );
}

/** Move focus to the first field in a scope that is marked as having failed. */
function focusFirstError(
  scope: HTMLElement,
  focusingError: { current: boolean },
) {
  const field = scope.querySelector<HTMLElement>(
    '.js-field[data-error="true"]',
  );

  if (!field) {
    return;
  }

  const element = firstVisible(
    field.querySelectorAll<HTMLElement>(".js-field-el"),
  );

  focusingError.current = true;
  element?.focus();
  focusingError.current = false;
}

/*
 * The message sits above the step, which can be a long way above the button
 * that was just pressed. The theme left it there; a failure with no field to
 * point at is brought into view instead.
 */
function scrollToMessage(ref: { current: HTMLElement | null }) {
  requestAnimationFrame(() =>
    ref.current?.scrollIntoView({ behavior: "smooth", block: "center" }),
  );
}
